/**
 * RelatedRecords — C-4 event-linking engine ("Related Records").
 *
 * A reusable cross-module panel that shows every quality record linked to the
 * current one (Deviation ↔ CAPA ↔ OOS ↔ Risk ↔ Change Control), lets the user
 * link another by its number, and remove user-created links. Implicit,
 * FK-derived links (e.g. a deviation's CAPA) are shown read-only.
 *
 * Backed by /api/v1/events/{type}/{id}/related (cc_impact.cross_reference_graph).
 * Drop it onto any event-detail screen:
 *   <RelatedRecords entityType="DEVIATION" entityId={d.deviationId} />
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import styles from './RelatedRecords.module.css';

export interface RelatedItem {
  edge_id: string | null;
  removable: boolean;
  edge_kind: string;
  confidence: string;
  entity_type: string;
  entity_id: string;
  number: string | null;
  title: string | null;
  status: string | null;
  path: string;
}
interface RelatedResp { items: RelatedItem[]; total: number }

const TYPE_LABEL: Record<string, string> = {
  DEVIATION: 'Deviation', CAPA: 'CAPA', OOS: 'OOS', RISK: 'Risk', CHANGE_CONTROL: 'Change Control',
};
const KIND_LABEL: Record<string, string> = {
  RELATED: 'Related', CAUSED_BY: 'Caused by', RESULTED_IN: 'Resulted in',
  DUPLICATE_OF: 'Duplicate of', REFERENCES: 'References', FK: 'Linked',
};
// Typed route patterns so navigation stays inside the SPA router.
const ROUTE_PATTERN: Record<string, string> = {
  DEVIATION: '/deviations/$id', CAPA: '/capa/$id', OOS: '/oos/$id',
  RISK: '/risks/$id', CHANGE_CONTROL: '/changes',
};

export function RelatedRecords({ entityType, entityId }: { entityType: string; entityId: string }) {
  const slug = entityType.toLowerCase();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ['events', slug, entityId, 'related'];

  const q = useQuery<RelatedResp>({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiFetch<RelatedResp>(`/api/v1/events/${slug}/${encodeURIComponent(entityId)}/related`, { signal }),
    enabled: !!entityId,
    staleTime: 30_000,
  });
  const add = useMutation({
    mutationFn: (body: { targetType: string; targetNumber: string; edgeKind: string }) =>
      apiFetch(`/api/v1/events/${slug}/${encodeURIComponent(entityId)}/related`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const remove = useMutation({
    mutationFn: (edgeId: string) =>
      apiFetch(`/api/v1/events/${slug}/${encodeURIComponent(entityId)}/related/${encodeURIComponent(edgeId)}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  const [open, setOpen] = useState(false);
  const [targetType, setTargetType] = useState('CAPA');
  const [targetNumber, setTargetNumber] = useState('');
  const [edgeKind, setEdgeKind] = useState('RELATED');
  const [msg, setMsg] = useState<string | null>(null);

  const items = q.data?.items ?? [];

  async function doAdd() {
    setMsg(null);
    const num = targetNumber.trim();
    if (!num) { setMsg('Enter the record number to link.'); return; }
    try {
      await add.mutateAsync({ targetType, targetNumber: num, edgeKind });
      setTargetNumber('');
      setOpen(false);
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : 'Could not link the record.');
    }
  }
  async function doRemove(edgeId: string) {
    setMsg(null);
    try { await remove.mutateAsync(edgeId); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not remove the link.'); }
  }
  function go(it: RelatedItem) {
    const pat = ROUTE_PATTERN[it.entity_type];
    if (!pat) return;
    if (pat.includes('$id')) navigate({ to: pat, params: { id: it.entity_id } });
    else navigate({ to: pat });
  }

  return (
    <section className={styles.panel} aria-labelledby="related-records">
      <header className={styles.head}>
        <h2 id="related-records" className={styles.title}>Related Records</h2>
        <span className={styles.count}>{items.length}</span>
        <button
          type="button"
          className={styles.action}
          onClick={() => { setOpen((v) => !v); setMsg(null); }}
        >
          {open ? 'Close' : '+ Link a record'}
        </button>
      </header>

      {open && (
        <div className={styles.addBox} role="group" aria-label="Link a record">
          <select aria-label="Record type" value={targetType} onChange={(e) => setTargetType(e.target.value)} className={styles.select}>
            <option value="DEVIATION">Deviation</option>
            <option value="CAPA">CAPA</option>
            <option value="OOS">OOS</option>
            <option value="RISK">Risk</option>
            <option value="CHANGE_CONTROL">Change Control</option>
          </select>
          <input
            type="text"
            value={targetNumber}
            onChange={(e) => setTargetNumber(e.target.value)}
            placeholder="e.g. CAPA-2026-0015"
            className={styles.input}
          />
          <select aria-label="Relationship" value={edgeKind} onChange={(e) => setEdgeKind(e.target.value)} className={styles.select}>
            <option value="RELATED">Related</option>
            <option value="CAUSED_BY">Caused by</option>
            <option value="RESULTED_IN">Resulted in</option>
            <option value="DUPLICATE_OF">Duplicate of</option>
            <option value="REFERENCES">References</option>
          </select>
          <button type="button" className={styles.linkBtn} onClick={() => void doAdd()} disabled={add.isPending}>
            {add.isPending ? 'Linking…' : 'Link'}
          </button>
        </div>
      )}
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      {q.isLoading && <div className={styles.empty}>Loading related records…</div>}
      {!q.isLoading && items.length === 0 && !open && (
        <div className={styles.empty}>
          No related records yet. Link the deviations, CAPAs, OOS, risks, or change controls connected to this one.
        </div>
      )}
      {items.length > 0 && (
        <ul className={styles.list}>
          {items.map((it) => (
            <li key={it.edge_id ?? `${it.entity_type}:${it.entity_id}`} className={styles.row}>
              <span className={styles.kind}>{KIND_LABEL[it.edge_kind] ?? it.edge_kind}</span>
              <span className={styles.typeTag}>{TYPE_LABEL[it.entity_type] ?? it.entity_type}</span>
              <button type="button" className={styles.recBtn} onClick={() => go(it)}>
                <span className={styles.mono}>{it.number ?? it.entity_id.slice(0, 8)}</span>
                {it.title ? ` · ${it.title}` : ''}
              </button>
              {it.status && <span className={styles.status}>{it.status}</span>}
              {it.removable && it.edge_id ? (
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => void doRemove(it.edge_id!)}
                  disabled={remove.isPending}
                  aria-label="Remove link"
                  title="Remove link"
                >×</button>
              ) : (
                <span className={styles.derived} title="Derived from a record reference">linked</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
